import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Message from 'components/Message';
import SmartRateDemandUserIcon from 'components/SmartRateDemandUserIcon/SmartRateDemandUserIcon';
import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';

import {
  MAXIMIZES_REVENUE_DEMAND_CHECK_ICON,
  MAXIMIZES_REVENUE_DEMAND_CLASS_NAME,
  MAXIMIZES_REVENUE_DEMAND_SEATS,
} from './MaximizesRevenuePanel.constants';
import { useMaximizesRevenueDemandAnimation } from './MaximizesRevenueDemandIllustration.hooks';
import messages from './MaximizesRevenuePanel.messages';
import './MaximizesRevenueDemandIllustration.scss';

const { block, element } = bem('maximizes-revenue-demand-illustration');

const CLASS_TIME_MESSAGES = {
  first: messages.demandIllustrationClassTime,
  second: messages.demandIllustrationClassTimeSecond,
};

const DEMAND_SUBTITLE_MESSAGES = {
  highDemand: messages.demandIllustrationHighDemandSubtitle,
  lowDemand: messages.demandIllustrationLowDemandSubtitle,
};

const LOWER_EARNINGS_MESSAGES = {
  first: messages.demandIllustrationLowerEarningsAmount,
  second: messages.demandIllustrationLowerEarningsAmountSecond,
};

const HIGHER_EARNINGS_MESSAGES = {
  first: messages.demandIllustrationHigherEarningsAmount,
};

const seatPositionPropType = PropTypes.shape({
  top: PropTypes.string.isRequired,
  left: PropTypes.string.isRequired,
});

const DemandSeat = ({ isGreen, isVisible, style }) => (
  <span
    className={classNames(element('seat'), {
      [element('seat', { visible: true })]: isVisible,
      [element('seat', { green: true })]: isGreen,
    })}
    style={style}>
    <SmartRateDemandUserIcon className={element('seat-icon')} />
  </span>
);

DemandSeat.propTypes = {
  isGreen: PropTypes.bool,
  isVisible: PropTypes.bool.isRequired,
  style: seatPositionPropType.isRequired,
};

DemandSeat.defaultProps = {
  isGreen: false,
};

const DemandCircle = ({ checkmarkSide, circleVariant, earningsAmountMessage, labelMessage, seats, showEarnings }) => (
  <div className={element('group')}>
    <div className={element('circle')}>
      <div
        className={classNames(element('circle-fill'), {
          [element('circle-fill', { green: true })]: circleVariant === 'green',
        })}
      />
      {checkmarkSide && (
        <img
          alt=""
          aria-hidden
          className={classNames(
            element('checkmark'),
            element('checkmark', { [checkmarkSide]: true }),
            element('checkmark', { visible: true })
          )}
          src={MAXIMIZES_REVENUE_DEMAND_CHECK_ICON}
        />
      )}
      {seats.map(seat => (
        <DemandSeat key={seat.id} isGreen={seat.isGreen} isVisible={seat.isVisible} style={seat.style} />
      ))}
    </div>
    <div className={element('group-footer')}>
      <Message as="p" className={element('group-label')} message={labelMessage} />
      <div
        className={classNames(element('earnings-details'), {
          [element('earnings-details', { visible: true })]: showEarnings,
        })}>
        <Message as="p" className={element('earnings-caption')} message={messages.demandIllustrationTotalEarnings} />
        <Message
          as="p"
          className={classNames(element('earnings-amount'), {
            [element('earnings-amount', { highlight: true })]: circleVariant === 'green',
          })}
          message={earningsAmountMessage}
        />
      </div>
    </div>
  </div>
);

DemandCircle.propTypes = {
  checkmarkSide: PropTypes.oneOf(['left', 'right']),
  circleVariant: PropTypes.oneOf(['blue', 'green']).isRequired,
  earningsAmountMessage: intlMessage,
  labelMessage: intlMessage.isRequired,
  seats: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      isGreen: PropTypes.bool.isRequired,
      isVisible: PropTypes.bool.isRequired,
      style: seatPositionPropType.isRequired,
    })
  ).isRequired,
  showEarnings: PropTypes.bool.isRequired,
};

DemandCircle.defaultProps = {
  checkmarkSide: null,
  earningsAmountMessage: null,
};

const buildSeatState = ({ greenSeatIds, visibleSeatIds }) =>
  MAXIMIZES_REVENUE_DEMAND_SEATS.map(seat => {
    const isVisible = visibleSeatIds.includes(seat.id);

    return {
      id: seat.id,
      isGreen: greenSeatIds.includes(seat.id) && isVisible,
      isVisible,
      style: { top: `${seat.top}%`, left: `${seat.left}%` },
    };
  });

const MaximizesRevenueDemandIllustration = () => {
  const prefersReducedMotion = useMemo(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  const {
    checkmarkSide,
    classTimeKey,
    demandSubtitleKey,
    higherEarningsKey,
    leftCircleVariant,
    leftGreen,
    leftVisible,
    lowerEarningsKey,
    rightCircleVariant,
    rightGreen,
    rightVisible,
    showEarnings,
  } = useMaximizesRevenueDemandAnimation({ prefersReducedMotion });

  const leftSeats = buildSeatState({
    greenSeatIds: leftGreen,
    visibleSeatIds: leftVisible,
  });

  const rightSeats = buildSeatState({
    greenSeatIds: rightGreen,
    visibleSeatIds: rightVisible,
  });

  return (
    <div aria-hidden className={block()}>
      <div className={element('class')}>
        <p className={element('class-time')}>
          <Message as="span" message={CLASS_TIME_MESSAGES[classTimeKey]} />
          {` - ${MAXIMIZES_REVENUE_DEMAND_CLASS_NAME}`}
        </p>
        <Message as="p" className={element('class-demand')} message={DEMAND_SUBTITLE_MESSAGES[demandSubtitleKey]} />
      </div>

      <div className={element('comparison')}>
        <DemandCircle
          checkmarkSide={showEarnings && checkmarkSide === 'left' ? 'left' : null}
          circleVariant={leftCircleVariant}
          earningsAmountMessage={LOWER_EARNINGS_MESSAGES[lowerEarningsKey]}
          labelMessage={messages.demandIllustrationLowerCredits}
          seats={leftSeats}
          showEarnings={showEarnings}
        />
        <Message as="span" className={element('versus')} message={messages.demandIllustrationVersus} />
        <DemandCircle
          checkmarkSide={showEarnings && checkmarkSide === 'right' ? 'right' : null}
          circleVariant={rightCircleVariant}
          earningsAmountMessage={HIGHER_EARNINGS_MESSAGES[higherEarningsKey]}
          labelMessage={messages.demandIllustrationHigherCredits}
          seats={rightSeats}
          showEarnings={showEarnings}
        />
      </div>

      <Message as="p" className={element('disclaimer')} message={messages.demandIllustrationDisclaimer} />
    </div>
  );
};

export default MaximizesRevenueDemandIllustration;
